
/**
 * Badge Service - Dynamic App Badge and Title Updates
 * Uses Badging API for PWA badge and document title updates
 */

interface BadgeOptions {
  portfolioValue?: number;
  change24h?: number;
  tokenCount?: number;
  alertCount?: number;
}

class BadgeService {
  private isSupported = false;
  private currentBadge: number = 0;

  /**
   * Initialize badge service
   */
  init(): boolean {
    // Check Badging API support (Chrome 81+, Edge 81+, Safari 16.4+)
    this.isSupported = 'setAppBadge' in navigator && 'clearAppBadge' in navigator;

    if (this.isSupported) {
      console.log('📛 Badge API supported');
    } else {
      console.log('📛 Badge API not supported in this browser');
    }

    return this.isSupported;
  }

  /**
   * Check if badge API is supported
   */
  isBadgeSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Update app badge and title based on portfolio performance
   */
  async updatePortfolioBadge(options: BadgeOptions): Promise<void> {
    const { portfolioValue, change24h, tokenCount, alertCount } = options;

    // Update document title with performance indicator
    if (change24h !== undefined) {
      const emoji = change24h >= 0 ? '📈' : '📉';
      const sign = change24h >= 0 ? '+' : '';
      const formattedChange = `${sign}${change24h.toFixed(1)}%`;

      document.title = `${emoji} Edouard.ai - ${formattedChange}`;
    } else {
      document.title = 'Edouard.ai - Portfolio Management';
    }

    // Update app badge
    if (this.isSupported) {
      try {
        // Badge shows alert count if available, otherwise token count
        const badgeCount = alertCount !== undefined && alertCount > 0
          ? alertCount
          : tokenCount || 0;

        if (badgeCount > 0) {
          await (navigator as any).setAppBadge(badgeCount);
          this.currentBadge = badgeCount;
        } else {
          await this.clearBadge();
        }
      } catch (error) {
        console.error('Error updating app badge:', error);
      }
    }
  }

  /**
   * Update badge with alert count
   */
  async updateAlertBadge(alertCount: number): Promise<void> {
    if (!this.isSupported) return;

    try {
      if (alertCount > 0) {
        await (navigator as any).setAppBadge(alertCount);
        this.currentBadge = alertCount;
        document.title = `🔔 Edouard.ai - ${alertCount} alert${alertCount > 1 ? 's' : ''}`;
      } else {
        await this.clearBadge();
      }
    } catch (error) {
      console.error('Error updating alert badge:', error);
    }
  }

  /**
   * Clear app badge
   */
  async clearBadge(): Promise<void> {
    if (!this.isSupported) return;

    try {
      await (navigator as any).clearAppBadge();
      this.currentBadge = 0;
      document.title = 'Edouard.ai - Portfolio Management';
    } catch (error) {
      console.error('Error clearing app badge:', error);
    }
  }

  /**
   * Get current badge count
   */
  getCurrentBadge(): number {
    return this.currentBadge;
  }

  /**
   * Update title with custom message
   */
  updateTitle(message: string, emoji?: string): void {
    const prefix = emoji ? `${emoji} ` : '';
    document.title = `${prefix}Edouard.ai - ${message}`;
  }

  /**
   * Reset title to default
   */
  resetTitle(): void {
    document.title = 'Edouard.ai - Portfolio Management';
  }
}

// Singleton instance
export const badgeService = new BadgeService();
